// The budget explorer: DoW RDT&E projects and procurement lines from the President's Budget
// justification books, with what changed since the current year. /budget/<id> opens a line's panel.
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { api, qs } from "../api/client";
import type { BudgetList, BudgetStatus, BudgetSummary } from "../api/types";
import { useSelectedKey, useUI } from "../app/context";
import { Badge } from "../components/Badge";
import { Sparkline } from "../components/FundingChart";
import { GlobalSearch } from "../components/GlobalSearch";
import { changeWords, fy, lineRef, money } from "../lib/format";

const STATUS: [BudgetStatus, string][] = [
  ["growing", "Growing"],
  ["new", "New starts"],
  ["cut", "Cut"],
  ["ending", "Ending"],
  ["steady", "Steady"],
];
const SORTS: [string, string][] = [
  ["change", "Largest increase"],
  ["cut", "Largest cut"],
  ["request", "Largest request"],
  ["records", "Most opportunities"],
  ["name", "Name"],
];
const PAGE = 60;

export function Budget() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const { openPanel } = useUI();
  const filters = {
    q: params.get("q") ?? "",
    service: params.get("service") ?? "",
    kind: params.get("kind") ?? "",
    status: params.get("status") ?? "",
    sort: params.get("sort") ?? "change",
    tied: params.get("tied") ?? "",
  };
  const [text, setText] = useState(filters.q);
  const set = (k: keyof typeof filters, v: string) => {
    const next = new URLSearchParams(params);
    if (v && !(k === "sort" && v === "change")) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  useEffect(() => {
    const t = window.setTimeout(() => text.trim() !== filters.q && set("q", text.trim()), 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  // /budget/<id> opens that line (ids contain colons, so they are the rest of the path).
  const opened = decodeURIComponent(location.pathname.replace(/^\/budget\/?/, ""));
  useEffect(() => {
    if (opened) openPanel({ kind: "budget", id: opened });
  }, [opened, openPanel]);
  const list = useInfiniteQuery({
    queryKey: ["budget", filters],
    queryFn: ({ pageParam }) => api<BudgetList>(`/api/budget${qs({ ...filters, offset: pageParam, limit: PAGE })}`),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (pages.length * PAGE < last.total ? pages.length * PAGE : undefined),
    placeholderData: keepPreviousData,
  });
  const d = list.data?.pages[0];
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const e = d?.editions[0];
  const prev = d?.editions[1];
  return (
    <>
      <div className="top">
        <div className="date">
          President’s Budget {e ? "FY" + e : ""}
          {prev ? ` compared with FY${prev}` : ""}
        </div>
        <GlobalSearch />
      </div>
      <h1>Budget</h1>
      {d ? null : <p className="empty">Loading the budget…</p>}
      <div className="tools budget-tools">
        <input className="filter" id="bq" type="search" placeholder="Search titles, program elements, plans, performers" aria-label="Search the budget" value={text} onChange={(ev) => setText(ev.target.value)} />
        <select id="b-service" aria-label="Service or agency" value={filters.service} onChange={(ev) => set("service", ev.target.value)}>
          <option value="">All services and agencies</option>
          {(d?.services ?? []).map(([n, c]) => (
            <option key={n} value={n}>
              {n} ({c})
            </option>
          ))}
        </select>
        <div className="seg" role="group" aria-label="Kind">
          {[
            ["", "All"],
            ["rdte", "RDT&E"],
            ["proc", "Procurement"],
            ["om", "O&M"],
            ["cj", "Health"],
          ].map(([v, l]) => (
            <button key={v} data-bkind={v} aria-pressed={filters.kind === v} onClick={() => set("kind", v!)}>
              {l}
            </button>
          ))}
        </div>
        <select id="b-sort" aria-label="Sort" value={filters.sort} onChange={(ev) => set("sort", ev.target.value)}>
          {SORTS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>
      <div className="chips-row" role="group" aria-label="Change since last year">
        <button className="chip-btn" data-bstatus="" aria-pressed={!filters.status} onClick={() => set("status", "")}>
          Everything
        </button>
        {STATUS.map(([v, l]) => (
          <button key={v} className="chip-btn" data-bstatus={v} aria-pressed={filters.status === v} onClick={() => set("status", v)}>
            {l}
            {d?.statuses[v] != null ? <small> {d.statuses[v]}</small> : null}
          </button>
        ))}
        <button className="chip-btn" data-btied aria-pressed={!!filters.tied} onClick={() => set("tied", filters.tied ? "" : "true")}>
          With opportunities{d ? <small> {d.tied}</small> : null}
        </button>
        {d ? <span className="muted-s count">{d.total.toLocaleString()} lines</span> : null}
      </div>
      <div className="rows" id="brows">
        {items.map((b) => (
          <BudgetRow key={b.id} b={b} />
        ))}
        {d && !items.length ? <p className="empty">Nothing in the budget matches. Try fewer words, or another service.</p> : null}
      </div>
      {list.hasNextPage ? (
        <button className="more-link" id="bmore-btn" disabled={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
          Show more ({(d!.total - items.length).toLocaleString()} left)
        </button>
      ) : null}
    </>
  );
}

function tiedWords(r: { linked: number; possible: number }) {
  const n = r.linked + r.possible;
  return n ? `${n} opportunit${n === 1 ? "y" : "ies"}${r.linked ? "" : " · possible"}` : null;
}

function BudgetRow({ b }: { b: BudgetSummary }) {
  const { openPanel } = useUI();
  const selected = useSelectedKey() === b.id;
  const where = [
    ...(b.kind === "cj"
      ? [b.project ? b.number_title : ""]
      : [lineRef(b), b.project ? `Project ${b.project}` : "", b.budget_activity ? `BA ${b.budget_activity}` : "", b.budget_activity_title]),
  ]
    .filter(Boolean)
    .join(" · ");
  const c = changeWords(b);
  const open = () => openPanel({ kind: "budget", id: b.id });
  return (
    <div className="row budget" data-budget={b.id} role="button" tabIndex={0} aria-selected={selected} onClick={open} onKeyDown={(e) => e.key === "Enter" && open()}>
      <Badge agency={b.agency + " " + b.service} small />
      <div style={{ minWidth: 0 }}>
        <div className="t">{b.title}</div>
        <div className="m">
          {b.service}
          {" "}
          {where}
          {b.kind !== "cj" && b.project && b.number_title !== b.title ? (
            <>
              {" "}
              <span className="muted">{b.number_title}</span>
            </>
          ) : null}
          {b.opportunities && b.opportunities.linked + b.opportunities.possible ? (
            <>
              {" "}
              <span className="fund linked" title={`${b.opportunities.linked} linked, ${b.opportunities.possible} possibly linked open or upcoming records`}>
                {tiedWords(b.opportunities)}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="amt">
        <b>{money(b.request)}</b>
        <span>{fy(b.edition)} request</span>
      </div>
      <div className="fitcol">
        {c.text ? <span className={`chg ${c.tone}`}>{c.text}</span> : null}
        <Sparkline b={b} />
      </div>
    </div>
  );
}
